import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { shallowEqual } from 'react-redux'
import { FormikProvider, useFormik } from 'formik'

import { Alert, Loader } from '@/ui'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import { OnboardingStickyNav } from '@/features/onboarding/shared/OnboardingStickyNav'

import { fetchAllQuarterlyTaxEstimateDetailsIfNeeded } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'

import { ANALYTICS_SURFACES } from '../analytics/compliance-analytics.constants'
import { useComplianceAnalytics } from '../analytics/use-compliance-analytics'
import { fetchIntake, submitIntake } from '../compliance.actions'
import {
  selectAnswers,
  selectNextQuarterlyPaymentDueDates,
  selectSectionIndex,
} from '../compliance.selectors'
import { setAnswer, setSectionIndex } from '../compliance.slice'
import {
  IntakeSection,
  QUARTERLY_DUE_DATES_SHOWN,
} from '../components/IntakeSection'
import { prepopulateValues } from '../components/IntakeQuestion'
import { ProgressSidebar } from '../components/ProgressSidebar'
import {
  COMPLIANCE_PATHS,
  FETCH_INTAKE_KEY,
  FUNNEL_MAX_W_CLS,
  FUNNEL_PAGE_CLS,
  HAS_ENTITY,
  isNotApplicableAnswer,
  TAX_TREATMENTS,
} from '../lib/constants'
import type { IntakeAnswers } from '../lib/types'
import type { QuestionDef } from '../questions/types'
import {
  isAnswered,
  isQuestionAnswered,
  isSectionComplete,
  navSections,
  QUESTIONS,
  visibleQuestions,
} from '../questions/registry'

/** Legacy per-state answers were sometimes stored as a bare string rather than
 *  a `{ [state]: value }` map — normalize those once, up front, before they
 *  become Formik's initial field state (nested field names like
 *  `${key}.${state}` need a real object at `${key}` to write into). */
const PER_STATE_KEYS = new Set(
  QUESTIONS.filter(
    (q) => q.input === 'per_state_date' || q.input === 'per_state_select'
  ).map((q) => q.key)
)

/** Single-select domains. A stored answer outside its domain would count
 *  toward section completion while rendering as nothing selected. */
const SINGLE_SELECT_VALUES = new Map(
  QUESTIONS.filter((q) => q.input === 'single' && q.options).map((q) => [
    q.key,
    new Set(q.options?.map((o) => o.value)),
  ])
)

/**
 * Whether a value arriving for `def` is its mirror of the question it
 * prepopulates from rather than something the user picked.
 *
 * The mirror writes through Formik exactly as a click does, so the value is all
 * there is to tell them apart. A user who picks precisely the source's states
 * loses that one event, which is the cheaper error: the mirror fires on mount
 * and on every edit of the source, so counting it inflates the question's
 * answer rate for everyone.
 */
const isSeededFromSource = (
  def: QuestionDef,
  answers: IntakeAnswers,
  value: unknown
): boolean => {
  if (!def.prepopulateFrom) return false
  const seed = prepopulateValues(answers[def.prepopulateFrom]) ?? []
  return shallowEqual(seed, value)
}

/** The page itself doesn't scroll (Layout's `<main>` does, via overflow-y-auto),
 *  so `window.scrollTo` is a no-op here — scroll that element instead. */
const scrollContentToTop = (): void => {
  document.querySelector('main')?.scrollTo({ top: 0 })
}

const normalizeInitialAnswers = (answers: IntakeAnswers): IntakeAnswers => {
  const next = { ...answers }
  const states = (next.licensed_states as string[] | undefined) ?? []
  for (const key of PER_STATE_KEYS) {
    const value = next[key]
    if (typeof value === 'string') {
      next[key] = states[0] ? { [states[0]]: value } : {}
    }
  }
  for (const [key, values] of SINGLE_SELECT_VALUES) {
    const value = next[key]
    if (typeof value === 'string' && value !== '' && !values.has(value)) {
      delete next[key]
    }
  }
  return next
}

const countAnswered = (answers: IntakeAnswers): number =>
  QUESTIONS.filter((q) => isQuestionAnswered(q, answers)).length

export const ComplianceIntake: React.FC = () => {
  const dispatch = useAppDispatch()
  const loading = useReselector(getIsFetchingOrNotStarted, FETCH_INTAKE_KEY)

  useEffect(() => {
    dispatch(fetchIntake())
    // Real IRS deadlines for the practice-stage summary card. `IfNeeded`, so
    // this is a no-op when another surface has already loaded them, and the
    // card degrades to no dates rather than blocking on it.
    dispatch(fetchAllQuarterlyTaxEstimateDetailsIfNeeded())
  }, [dispatch])

  if (loading) {
    return (
      <div className="flex w-full items-center justify-center py-24">
        <Loader size={40} />
      </div>
    )
  }

  return <ComplianceIntakeForm />
}

/** Mounts once `fetchIntake` resolves, so Formik seeds its initial field
 *  state from the real fetched answers instead of the slice's empty default. */
const ComplianceIntakeForm: React.FC = () => {
  const dispatch = useAppDispatch()
  // Fetched by the shell above; empty until it lands, which the card handles.
  const quarterlyDueDates = useReselector(
    selectNextQuarterlyPaymentDueDates,
    QUARTERLY_DUE_DATES_SHOWN
  )
  const navigate = useNavigate()

  const answers = useReselector(selectAnswers)
  const sectionIndex = useReselector(selectSectionIndex)

  const [submitting, setSubmitting] = useState(false)
  // Submit is the only thing that persists the round — and the only thing that
  // fills the signup-owned `FinancialProfile` columns checkout reads. A failure
  // that just stops the spinner leaves the user stranded with nothing saved.
  const [submitFailed, setSubmitFailed] = useState(false)
  // Set when the user tries to continue past an incomplete section — shows
  // per-question error messages instead of just blocking the button.
  const [showErrors, setShowErrors] = useState(false)
  const [initialAnswers] = useState(() => normalizeInitialAnswers(answers))
  const [initialAnsweredCount] = useState(() => countAnswered(initialAnswers))
  const resumed = initialAnsweredCount > 0
  const startedRef = useRef(false)
  const sectionEnteredAtRef = useRef(Date.now())

  const {
    trackViewedIntakePage,
    trackStartedIntake,
    trackAnsweredQuestion,
    trackCompletedIntakeSection,
    trackBlockedOnIncompleteSection,
    trackFailedIntakeSubmit,
  } = useComplianceAnalytics(ANALYTICS_SURFACES.SignupFunnel)

  // Formik owns each question's live field state (required by the DS Form.*
  // molecules); IntakeQuestion mirrors every change into `answers` below, so
  // Redux stays the source of truth for section nav, reveal logic, and submit.
  const formik = useFormik({
    initialValues: initialAnswers,
    onSubmit: () => {},
  })

  const sections = navSections()
  const boundedIndex = Math.max(0, Math.min(sectionIndex, sections.length - 1))
  const currentSection = sections[boundedIndex]
  const isLast = boundedIndex === sections.length - 1
  const canAdvance = isSectionComplete(currentSection, answers)

  useEffect(() => {
    trackViewedIntakePage({
      resumed,
      answered_count: initialAnsweredCount,
      section_index: boundedIndex,
    })
    // The visit is reported once, from the state it arrived in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The furthest section reachable via nav clicks — the first incomplete
  // section blocks jumping any further ahead, though it's still reachable
  // itself (that's where the user would naturally continue).
  const firstIncompleteIndex = sections.findIndex(
    (s) => !isSectionComplete(s, answers)
  )
  const maxReachableIndex =
    firstIncompleteIndex === -1 ? sections.length - 1 : firstIncompleteIndex

  const invalidQuestions = visibleQuestions(currentSection, answers).filter(
    (q) => !q.optional && !isQuestionAnswered(q, answers)
  )
  const incompleteCount = showErrors ? invalidQuestions.length : 0

  const goTo = (i: number): void => {
    const clamped = Math.max(0, Math.min(i, maxReachableIndex))
    // Forward nav starts the next section at the top; back nav keeps
    // whatever scroll position the user had.
    if (clamped > boundedIndex) scrollContentToTop()
    dispatch(setSectionIndex(clamped))
    setShowErrors(false)
    sectionEnteredAtRef.current = Date.now()
  }

  const onNext = (): void => {
    if (!canAdvance) {
      setShowErrors(true)
      trackBlockedOnIncompleteSection({
        section: currentSection,
        missing_question_keys: invalidQuestions.map((q) => q.key),
      })
      const firstInvalid = invalidQuestions[0]
      if (firstInvalid) {
        document
          .getElementById(`question-${firstInvalid.key}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      return
    }
    trackCompletedIntakeSection({
      section: currentSection,
      section_index: boundedIndex,
      question_count: visibleQuestions(currentSection, answers).length,
      duration_ms: Date.now() - sectionEnteredAtRef.current,
      error_shown: showErrors,
    })
    setShowErrors(false)
    if (isLast) {
      void onGenerate()
    } else {
      goTo(boundedIndex + 1)
    }
  }

  const onPrev = (): void => {
    if (boundedIndex > 0) goTo(boundedIndex - 1)
  }

  const onAnswer = (key: string, value: unknown): void => {
    // Every question mirrors its Formik value into Redux on mount as well as on
    // change, so an unchanged mirror is not an answer and must not be reported.
    const changed = !shallowEqual(answers[key], value)
    const wasAnswered = isAnswered(answers[key])
    const sectionQuestions = visibleQuestions(currentSection, answers)
    const questionIndex = sectionQuestions.findIndex((q) => q.key === key)
    const question = sectionQuestions[questionIndex]
    dispatch(setAnswer({ key, value }))
    if (changed && question && !isSeededFromSource(question, answers, value)) {
      if (!resumed && !startedRef.current) {
        startedRef.current = true
        trackStartedIntake({ first_question_key: key })
      }
      trackAnsweredQuestion({
        question_key: key,
        section: currentSection,
        section_index: boundedIndex,
        question_index: questionIndex,
        input_type: question.input,
        is_change: wasAnswered,
        answered_count: countAnswered({ ...answers, [key]: value }),
        value,
      })
    }
    // hiring_plan is only asked (and only meaningful) for solo practices —
    // DERIVED_ANSWERS fills it in as 'group' for everyone else, but only
    // when the key is still unanswered. Leaving a stale solo-era answer in
    // place after switching to a group size would permanently block that
    // derivation, which in turn blocks has_multiple_owners from ever
    // revealing again.
    if (key === 'practice_size' && value !== '1') {
      dispatch(setAnswer({ key: 'hiring_plan', value: undefined }))
    }
    // A courtesy to this session, not a durable write: submit omits undefined
    // keys, so the report's precedence is what resolves a contradiction.
    const clearAnswer = (cleared: string): void => {
      dispatch(setAnswer({ key: cleared, value: undefined }))
      formik.setFieldValue(cleared, undefined)
    }
    if (key === 'tax_treatment') {
      const electing =
        value === TAX_TREATMENTS.scorp || value === TAX_TREATMENTS.ccorp
      // Cleared rather than set to 'yes' so the derivation supplies it.
      if (electing && answers.has_entity !== undefined) {
        clearAnswer('has_entity')
      }
      // has_entity is cleared here rather than answered, so the branch below
      // cannot fire and the sentinel would survive into a revealed date field.
      if (electing && isNotApplicableAnswer(answers.entity_formed_at)) {
        clearAnswer('entity_formed_at')
      }
    }
    if (key === 'has_entity') {
      // The registration state also feeds FinancialProfile.homeState, which
      // falls back to practice_states then licensed_states, so clearing it here
      // does not block checkout.
      if (value === HAS_ENTITY.no || value === HAS_ENTITY.notsure) {
        for (const detail of [
          'entity_formed_at',
          'entity_registration_state',
        ]) {
          if (answers[detail] !== undefined) clearAnswer(detail)
        }
      }
      // A real date is theirs to keep; only the sentinel contradicts 'yes'.
      if (
        value === HAS_ENTITY.yes &&
        isNotApplicableAnswer(answers.entity_formed_at)
      ) {
        clearAnswer('entity_formed_at')
      }
    }
  }

  const onGenerate = async (): Promise<void> => {
    setSubmitting(true)
    setSubmitFailed(false)
    const outcome = await dispatch(submitIntake(answers))
    setSubmitting(false)
    if (outcome.ok) {
      navigate(COMPLIANCE_PATHS.report)
      return
    }
    setSubmitFailed(true)
    trackFailedIntakeSubmit({
      error_status: outcome.status,
      answered_count: countAnswered(answers),
    })
  }

  return (
    <FormikProvider value={formik}>
      <div className="flex w-full flex-col gap-6 pb-10">
        <div className="flex flex-col gap-3">
          {incompleteCount > 0 && (
            <div className={FUNNEL_PAGE_CLS}>
              <Alert
                variant="error"
                title="A few required questions are missing"
                description={
                  incompleteCount === 1
                    ? 'Answer the highlighted question below before continuing.'
                    : `Answer the ${incompleteCount} highlighted questions below before continuing.`
                }
              />
            </div>
          )}
          {submitFailed && (
            <div className={FUNNEL_PAGE_CLS}>
              <Alert
                variant="error"
                title="We couldn't save your answers"
                description="Something went wrong on our end. Your answers are still here, so try again."
              />
            </div>
          )}
          <div className={FUNNEL_PAGE_CLS}>
            <ProgressSidebar
              sections={[...sections]}
              currentIndex={boundedIndex}
              onJump={goTo}
              maxReachableIndex={maxReachableIndex}
            />
          </div>
        </div>

        <div className="mx-auto w-full max-w-2xl px-6 pb-12">
          <IntakeSection
            // Keyed so each section gets its own instance: `confirmedKeys`
            // seeds from the answers present at mount, and a shared instance
            // would seed once for the whole visit, letting a prepopulated
            // question in a later section collapse unseen.
            key={currentSection}
            section={currentSection}
            index={boundedIndex}
            total={sections.length}
            answers={answers}
            onAnswer={onAnswer}
            showErrors={showErrors}
            quarterlyDueDates={quarterlyDueDates}
          />
        </div>

        <OnboardingStickyNav
          onBack={boundedIndex > 0 ? onPrev : undefined}
          onContinue={onNext}
          backLabel="Back"
          continueLabel={isLast ? 'Get My Free Report' : 'Continue'}
          continueDisabled={submitting}
          unfixedOnMobile={false}
          className={FUNNEL_MAX_W_CLS}
        />
      </div>
    </FormikProvider>
  )
}
